import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { logout, selectAuth } from "../store/authSlice";
import { selectSupportUnread } from "../store/supportChatSlice";
import SupportIcon from "./icons/SupportIcon";
import "./Sidebar.css";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const MOBILE_BP = 960;

/* ───────────────── Icons ───────────────── */

const Icon: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 20,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const icons = {
  rewrite: (
    <Icon size={22}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </Icon>
  ),
  shield: (
    <Icon size={22}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </Icon>
  ),
  dashboard: (
    <Icon>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Icon>
  ),
  autowrite: (
    <Icon>
      <path d="M4 19h16" />
      <path d="M4 15h10" />
      <path d="M4 11h16" />
      <path d="M4 7h10" />
    </Icon>
  ),
  chat: (
    <Icon>
      <path d="M21 12a8.5 8.5 0 0 1-12.4 7.5L3 21l1.5-5.6A8.5 8.5 0 1 1 21 12z" />
    </Icon>
  ),
  support: <SupportIcon size={20} strokeWidth={1.8} />,
  profile: (
    <Icon>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </Icon>
  ),
  admin: (
    <Icon>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9 1.65 1.65 0 0 0 4.27 7.18l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 0 1 4 0v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.36.15.66.4.87.71.21.31.32.68.31 1.05V11h.09a2 2 0 0 1 0 4H21a1.65 1.65 0 0 0-1.51 1z" />
    </Icon>
  ),
  chevronLeft: (
    <Icon size={16}>
      <polyline points="15 18 9 12 15 6" />
    </Icon>
  ),
  chevronRight: (
    <Icon size={16}>
      <polyline points="9 18 15 12 9 6" />
    </Icon>
  ),
  chevronDown: (
    <Icon size={14}>
      <polyline points="6 9 12 15 18 9" />
    </Icon>
  ),
  menu: (
    <Icon>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="16" y2="12" />
      <line x1="4" y1="18" x2="12" y2="18" />
    </Icon>
  ),
  close: (
    <Icon>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </Icon>
  ),
  plus: (
    <Icon size={18}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </Icon>
  ),
  logout: (
    <Icon size={18}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </Icon>
  ),
};

/* ───────────────── Component ───────────────── */

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const [showBalanceDetails, setShowBalanceDetails] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user, isAuthenticated, isAdministrator } = useAppSelector(selectAuth);
  const supportUnread = useAppSelector(selectSupportUnread);

  const balance = user?.balance ?? 0;
  const bonus = user?.bonus ?? 0;
  const totalBalance = balance + bonus;

  /* mobile detection */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= MOBILE_BP);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  /* prevent body scroll when mobile drawer open */
  useEffect(() => {
    if (!isMobile) return;
    document.body.style.overflow = isMobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, isMobileOpen]);

  /* close mobile on Escape */
  useEffect(() => {
    if (!isMobileOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsMobileOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isMobileOpen]);

  /* close mobile drawer on route change */
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const primaryActions = useMemo(
    () => [
      {
        id: "rewrite",
        title: "Рерайт",
        hint: "Повышаем уникальность",
        icon: icons.rewrite,
        path: "/app",
        tone: "primary" as const,
      },
      {
        id: "antiplag",
        title: "Антиплагиат",
        hint: "Проверка работ",
        icon: icons.shield,
        path: "/reports",
        tone: "check" as const,
      },
    ],
    [],
  );

  const secondaryNav = useMemo(
    () => [
      { id: "dashboard", name: "Главная", path: "/dashboard", icon: icons.dashboard },
      { id: "autowrite", name: "Автонаписание", path: "/autowrite", icon: icons.autowrite },
      { id: "chat", name: "ChatGPT", path: "/chat", icon: icons.chat },
      {
        id: "support",
        name: "Поддержка",
        path: "/support",
        icon: icons.support,
        badge: supportUnread,
      },
      { id: "profile", name: "Профиль", path: "/profile", icon: icons.profile },
      ...(isAdministrator
        ? [{ id: "admin", name: "Админ-панель", path: "/admin", icon: icons.admin }]
        : []),
    ],
    [isAdministrator, supportUnread],
  );

  if (!isAuthenticated) return null;

  /** На мобилке сайдбар только выезжающий ящик — свёрнутый режим десктопа не нужен и ломает подписи. */
  const effectiveCollapsed = isCollapsed && !isMobile;

  const closeMobile = () => setIsMobileOpen(false);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleNavigation = (path: string) => {
    if (location.pathname !== path) navigate(path);
    if (isMobile) closeMobile();
  };

  /** Для пунктов-ссылок: переход делает сам <Link>, нам нужно лишь закрыть мобильный ящик. */
  const handleNavClick = () => {
    if (isMobile) closeMobile();
  };

  const handleLogout = () => {
    dispatch(logout());
    if (isMobile) closeMobile();
  };

  return (
    <>
      <button
        className="mobile-menu-btn"
        onClick={() => setIsMobileOpen(true)}
        aria-label="Открыть меню"
        aria-expanded={isMobileOpen}
        aria-controls="app-sidebar"
        type="button"
      >
        {icons.menu}
      </button>

      <div
        className={`sidebar-overlay ${isMobileOpen ? "open" : ""}`}
        onClick={closeMobile}
        aria-hidden={!isMobileOpen}
      />

      <aside
        id="app-sidebar"
        className={`sidebar ${effectiveCollapsed ? "collapsed" : ""} ${
          isMobileOpen ? "mobile-open" : ""
        }`}
      >
        {/* ── Header ── */}
        <div className={`sidebar-header ${isMobile ? "sidebar-header--mobile" : ""}`}>
          {!isMobile && (
            <button
              type="button"
              className="sidebar-toggle"
              onClick={onToggle}
              aria-label={isCollapsed ? "Развернуть меню" : "Свернуть меню"}
            >
              <span className="toggle-icon">
                {isCollapsed ? icons.chevronRight : icons.chevronLeft}
              </span>
            </button>
          )}

          <Link
            to="/"
            className={`sidebar-brand-link ${effectiveCollapsed ? "collapsed" : ""}`}
            aria-label="На главную"
          >
            <div className="sidebar-brand">
              <h2>Антиплагиату.НЕТ</h2>
            </div>
          </Link>

          <button
            type="button"
            className="mobile-close-btn"
            onClick={closeMobile}
            aria-label="Закрыть меню"
          >
            {icons.close}
          </button>
        </div>

        {/* ── Scrollable body ── */}
        <div className="sidebar-body" data-tour="sidebar-navigation">
          {/* ── Primary actions (Рерайт / Антиплагиат) ── */}
          <div className="sidebar-quick">
            {primaryActions.map((tile) => (
              <Link
                key={tile.id}
                to={tile.path}
                className={`sidebar-quick-tile ${
                  tile.tone === "check" ? "sidebar-quick-tile--check" : ""
                } ${isActive(tile.path) ? "active" : ""}`}
                onClick={handleNavClick}
                title={tile.title}
              >
                <span className="sidebar-quick-tile__icon">{tile.icon}</span>
                {!effectiveCollapsed && (
                  <span className="sidebar-quick-tile__text">
                    <span className="sidebar-quick-tile__title">{tile.title}</span>
                    <span className="sidebar-quick-tile__hint">{tile.hint}</span>
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* ── Secondary nav ── */}
          {!effectiveCollapsed && (
            <div className="sidebar-section-label">Меню</div>
          )}
          <nav className="sidebar-secondary-nav" aria-label="Основная навигация">
            {secondaryNav.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`nav-item nav-item--compact ${active ? "active" : ""}`}
                  onClick={handleNavClick}
                  title={item.name}
                >
                  <span className="nav-icon">
                    {item.icon}
                    {effectiveCollapsed && item.badge ? (
                      <span className="nav-badge nav-badge--dot">
                        {item.badge > 9 ? "9+" : item.badge}
                      </span>
                    ) : null}
                  </span>
                  {!effectiveCollapsed && (
                    <>
                      <span className="nav-name nav-name--solo">{item.name}</span>
                      {item.badge ? (
                        <span className="nav-badge">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      ) : null}
                    </>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* ── Balance card (compact) ── */}
        <div className="sidebar-user-info" data-tour="sidebar-balance">
          {!effectiveCollapsed ? (
            <div className="balance-card balance-card--compact">
              <div className="balance-top">
                <div>
                  <span className="balance-label">Баланс</span>
                  <div className="balance-total balance-total--sm">
                    {totalBalance.toLocaleString("ru-RU")} ₽
                  </div>
                </div>
                <button
                  type="button"
                  className={`balance-details-toggle ${
                    showBalanceDetails ? "open" : ""
                  }`}
                  onClick={() => setShowBalanceDetails((prev) => !prev)}
                  aria-label="Показать детали баланса"
                  aria-expanded={showBalanceDetails}
                >
                  {icons.chevronDown}
                </button>
              </div>

              {showBalanceDetails && (
                <div className="balance-meta">
                  <div className="balance-row balance-row--tight">
                    <span className="meta-label">Основной</span>
                    <span className="meta-value">
                      {balance.toLocaleString("ru-RU")} ₽
                    </span>
                  </div>
                  <div className="balance-row balance-row--tight">
                    <span className="meta-label">Бонусы</span>
                    <span className="meta-value">
                      {bonus.toLocaleString("ru-RU")} ₽
                    </span>
                  </div>
                  <div className="balance-subtext">
                    Рерайт списывается сначала с бонусов. Антиплагиат — только
                    с основного баланса.
                  </div>
                </div>
              )}

              <button
                type="button"
                className="primary-btn primary-btn--compact full-width"
                onClick={() => handleNavigation("/profile")}
              >
                Пополнить баланс
              </button>
            </div>
          ) : (
            <div className="user-info-compact">
              <div className="compact-balance">
                <span className="compact-amount">
                  {totalBalance.toLocaleString("ru-RU")} ₽
                </span>
                <span className="compact-label">баланс</span>
              </div>
              <button
                type="button"
                className="compact-topup-btn"
                title="Пополнить баланс"
                onClick={() => handleNavigation("/profile")}
                aria-label="Пополнить баланс"
              >
                {icons.plus}
              </button>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="sidebar-footer">
          <button
            type="button"
            className={`logout-btn ${effectiveCollapsed ? "collapsed" : ""}`}
            onClick={handleLogout}
            title="Выйти"
            aria-label="Выйти"
          >
            {icons.logout}
            {!effectiveCollapsed && <span>Выйти</span>}
          </button>

          {!effectiveCollapsed && (
            <div className="sidebar-legal-links">
              <Link to="/privacy" className="sidebar-privacy-link">
                Политика
              </Link>
              <span className="sidebar-legal-dot" aria-hidden>·</span>
              <Link to="/offer" className="sidebar-privacy-link">
                Оферта
              </Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
